import type { DataTableColumn } from '@/components/UI/DataTableV2/DataTable.model';
import { isDateFieldType, isSelectFieldType } from '@/components/UI/DataTableV2/DataTable.util';
import { _isNil } from '@/littledash';

/**
 * The family of sub-editor the shell renders for a given column. Tasks 4-6
 * implement the concrete `text`/`date`/`select` editors; `none` covers columns
 * that are read-only or otherwise not editable inline.
 */
export type SubEditorKind = 'text' | 'date' | 'select' | 'none';

/**
 * Resolves which sub-editor family a column maps to, mirroring the branching in
 * `cells/CellTypeRouter.tsx`. This is the single source of truth the shell uses
 * to decide which sub-editor to mount, kept pure so it can be unit-tested.
 *
 * @param column - The column to resolve editor kind for
 * @param isTableReadonly - Whether the table is in readonly mode
 * @returns The sub-editor kind; 'none' if the column is not editable
 */
export const resolveSubEditorKind = (column: DataTableColumn | undefined, isTableReadonly: boolean): SubEditorKind => {
  if (_isNil(column)) {
    return 'none';
  }

  if (isTableReadonly || column.locked || column.read_only) {
    return 'none';
  }

  switch (column.type) {
    case 'text':
    case 'number':
    case 'sampleDetail':
    case 'sampleComments':
      return 'text';

    case 'measurement':
      return (column.measurement?.is_calculated ?? false) ? 'none' : 'text';

    case 'timestamp':
    case 'timestampBaseline':
    case 'sampleCollectedAt':
    case 'sampleDoseTime':
      return 'date';

    case 'metadata': {
      const fieldType = column.metadata?.field_type;
      if (isDateFieldType(fieldType)) {
        return 'date';
      }
      if (isSelectFieldType(fieldType)) {
        return 'select';
      }
      return 'text';
    }

    default:
      return 'none';
  }
};
